import { useEffect, useRef } from "react";
import { getPlatform } from "@/lib/platform";
import { useSessionStore } from "@/store/session";
import { API_BASE_URL } from "@/api/client";
import { ANALYTICS_ENABLED_KEY } from "@/lib/storageKeys";

/** DUE-173: client-side event tracker. Buffers `track()` calls in memory
 * and flushes them in batches to `POST /api/events/batch` every 5s (or when
 * the tab goes to background), so screens fire-and-forget analytics without
 * a network round trip per event.
 *
 * Deliberately bypasses `api/client.ts`'s `request()`: that helper
 * zod-validates responses and re-authenticates on 401, neither of which
 * belongs on a best-effort telemetry path. A failed/rejected flush simply
 * drops its batch — there is no retry and no reauth. */

export const FEATURES = {
  natal: "natal",
  synastry: "synastry",
  composite: "composite",
  transits: "transits",
  paywall: "paywall",
  onboarding: "onboarding",
  share: "share",
  // DUE-213: daily-habit core's push channel (client-emitted counterpart
  // to the backend's `push.sent` / `push.paused_inactive`).
  push: "push",
  // Mood-of-the-day screen — mirrors backend `Feature.MOOD`.
  mood: "mood",
  // Design v2 / E3: the Today screen — mirrors backend `Feature.TODAY`.
  // Its own feature, not a corner of `transits`: `transits.*` is the
  // reading and its entitlement, `today.*` is the screen people navigate.
  today: "today",
  // DUE-270 Task 9: the You stack's screens — mirrors backend
  // `Feature.YOU`. `natal.*` counts the reading and its entitlement,
  // `you.*` counts what people look at once they have one.
  you: "you",
  // DUE-269: the partner's own screen — mirrors backend `Feature.PARTNER`.
  // Its own feature, not `today.*`: this is the day between two people,
  // not the user's own day.
  partner: "partner",
  // DUE-271: the lens preview (`03-FRIENDS.md` §5) — mirrors backend
  // `Feature.LENS`. Its own feature, not `synastry.*`: this is
  // relation-coloured copy gated on a labelled partner.
  lens: "lens",
  // DUE-411: the Relation screen's store hand-off — mirrors backend
  // `Feature.RELATION`. `relation.purchase_started {level, partner_id}`.
  relation: "relation",
  // The reading vote (👍/👎) under every LLM text — mirrors backend
  // `Feature.FEEDBACK`. Its own feature, not a corner of the surface it
  // sits on: one widget, eight screens, one number to read.
  feedback: "feedback",
  // DUE-318: the browser surface itself — mirrors backend `Feature.WEB`.
  web: "web",
  // DUE-319: email/Google/Apple/Telegram-widget sign-in and the keep-access
  // gate — mirrors backend `Feature.WEB_AUTH`
  // (`src/core/tracking.py::CLIENT_EVENT_TYPES`).
  webAuth: "web_auth",
} as const;

export const ACTIONS = {
  screenView: "screen_view",
  generate: "generate",
  share: "share",
  purchaseStarted: "purchase_started",
  // DUE-494: must exist in CLIENT_EVENT_TYPES (src/core/tracking.py).
  purchaseCancelled: "purchase_cancelled",
  purchaseFailed: "purchase_failed",
  // DUE-213: a delivered push notification was tapped open.
  opened: "opened",
  // DUE-213: a paywall CTA was shown/triggered from a teaser surface.
  paywallShown: "paywall_shown",
  // DUE-220: the post-natal-reading permission-ask funnel, both surfaces
  // (iOS auto-prompt, Telegram sheet — `NatalReadingPage.tsx`). Telegram's
  // `prompt_declined` includes dismissing the sheet (DUE-219: dismiss =
  // decline). Platform split rides the batch-level `platform` field.
  promptShown: "prompt_shown",
  promptAccepted: "prompt_accepted",
  promptDeclined: "prompt_declined",
  // Design v2 / E3 §3b/§3d: Today's two interactions.
  // `day_switched` carries `{offset, from_cache}` — `from_cache` is what
  // proves the week endpoint actually took the network out of a day
  // switch. `person_tapped` carries `{tier}`, the rank the server gave
  // the row the closest row named.
  //
  // Both pairs must exist in `src/core/tracking.py::CLIENT_EVENT_TYPES`:
  // a pair shipped from here but missing there is dropped silently, not
  // rejected, so nothing ever surfaces the mistake.
  daySwitched: "day_switched",
  personTapped: "person_tapped",
  // Design v2 / E4: how long the calc screen actually waited for
  // `POST /api/onboarding/complete`, in ms, plus the `outcome` it waited
  // for (`ok` / `conflict` / `error`). The roadmap names this as the
  // stage's metric: the screen holds for ~1.5s minimum, so the number is
  // what says whether the real wait is under that floor or well over it.
  //
  // NOTE: `(Feature.ONBOARDING, "calc_waited")` still has to be added to
  // `CLIENT_EVENT_TYPES` in `src/core/tracking.py` — until it is, the
  // batch endpoint drops this pair silently rather than rejecting it.
  calcWaited: "calc_waited",
  // DUE-270 Task 9: the You stack's screens — mirrors backend
  // `Action.CHAPTER_VIEWED` / `ARCHETYPE_VIEWED` / `READING_REGENERATED`.
  // `chapterViewed` fires once per (reading id, chapter) from
  // `NatalReadingPage.tsx`; `archetypeViewed` fires once per successful
  // load from `ArchetypePage.tsx`; `readingRegenerated` fires from the
  // stale-reading path (Task 8).
  chapterViewed: "chapter_viewed",
  archetypeViewed: "archetype_viewed",
  readingRegenerated: "reading_regenerated",
  // DUE-269: the partner's Today tab rendered; props say what it could
  // draw (`has_window`, `has_move`) and which half of the toggle.
  //
  // Must exist in `src/core/tracking.py::CLIENT_EVENT_TYPES` as
  // `(Feature.PARTNER, Action.TODAY_VIEWED)` — a pair shipped from here
  // but missing there is dropped silently, not rejected.
  todayViewed: "today_viewed",
  // DUE-284: `synastry.section_viewed` / `composite.section_viewed {section, lens}`
  // fire once per (reading id, section, lens) from `useSectionViewed`;
  // `lens.locked_tapped {surface}` when the locked LENS half is opened —
  // `surface: "synastry"` from the toggle, `"day"` from the Today tab's
  // locked row, which is the same funnel entered from the other side.
  sectionViewed: "section_viewed",
  lockedTapped: "locked_tapped",
  // DUE-285: `lens.reading_viewed {relation_type, has_words, cached}` — the
  // full lens read was on screen, once per (partner, reading).
  readingViewed: "reading_viewed",
  // DUE-286: `partner.day_viewed` — the partner day was on screen, once
  // per (partner, date).
  //
  // Must exist in `src/core/tracking.py::CLIENT_EVENT_TYPES` as
  // `(Feature.PARTNER, Action.DAY_VIEWED)` — a pair shipped from here but
  // missing there is dropped silently, not rejected.
  dayViewed: "day_viewed",
  // `feedback.shown {operation}` — the footer actually rendered, once per
  // generation. It is the denominator of the response rate, which is the
  // first number the feature has to clear; the vote itself is emitted by
  // the backend (`feedback.submitted`), the only party that knows it was
  // accepted, and must NOT be listed here.
  //
  // Must exist in `src/core/tracking.py::CLIENT_EVENT_TYPES` as
  // `(Feature.FEEDBACK, Action.SHOWN)` — a pair shipped from here but
  // missing there is dropped silently, not rejected.
  shown: "shown",
  // DUE-268: the Today screen rendered with a resolved stage body —
  // `{body, source}`, `source: "featured" | "weekday" | "fallback"`.
  // The fallback share is the featured-body dial's health number.
  //
  // Must exist in `src/core/tracking.py::CLIENT_EVENT_TYPES` as
  // `(Feature.TODAY, Action.VIEWED)` — a pair shipped from here but
  // missing there is dropped silently, not rejected.
  viewed: "viewed",
  // DUE-318: a redirect-paywall link was tapped {target: app_store | telegram}; mirrors backend `Action.PAYWALL_REDIRECT_CLICKED`. Emitted from `WebPaywallRedirect.tsx`.
  paywallRedirectClicked: "paywall_redirect_clicked",
  // DUE-319: `web_auth.started {method}` — a sign-in method was chosen from
  // the keep-access gate or the You tab's linked-accounts row.
  //
  // Must exist in `src/core/tracking.py::CLIENT_EVENT_TYPES` as
  // `(Feature.WEB_AUTH, Action.STARTED)` — a pair shipped from here but
  // missing there is dropped silently, not rejected.
  started: "started",
  // DUE-318: which share path the browser took {method: native | clipboard}; mirrors backend `Action.SHARE_METHOD`. Emitted from `lib/platform/web.ts`.
  shareMethod: "share_method",
  // DUE-329 §5: the Today pair teaser — ClosestRow's tag slot, tier-1
  // only, behind `today_pair_teaser`. `{partner_id, state}`.
  //
  // Must exist in `src/core/tracking.py::CLIENT_EVENT_TYPES` as
  // `(Feature.TODAY, Action.PAIR_TEASER_VIEWED/TAPPED)` — a pair shipped
  // from here but missing there is dropped silently, not rejected.
  pairTeaserViewed: "pair_teaser_viewed",
  pairTeaserTapped: "pair_teaser_tapped",
  // DUE-329 §6/§6.1: the share button under a served partner day, plain or
  // lens alike. `{partner_id, lens}` — §8. `share.partner_day_opened` is
  // backend-only (emitted from `GET /s/{hash}`) and has no client constant.
  //
  // Must exist in `src/core/tracking.py::CLIENT_EVENT_TYPES` as
  // `(Feature.SHARE, Action.PARTNER_DAY_CREATED)`.
  partnerDayCreated: "partner_day_created",
} as const;

interface TrackedEvent {
  feature: string;
  action: string;
  props?: Record<string, unknown>;
  client_ts: string;
}

/** Oldest-dropped cap: unauthenticated buffering (guest boot, expired JWT)
 * must not grow unbounded while events wait for a session. Value must not exceed
 * the backend batch limit (EventBatchIn.events max_length=50 in src/api/schemas/events.py)
 * or a >50 flush would 422 and be silently dropped. */
const MAX_BUFFER_SIZE = 50;
const FLUSH_INTERVAL_MS = 5000;

let buffer: TrackedEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let visibilityHandler: (() => void) | null = null;

function ensureFlushScheduled(): void {
  if (flushTimer === null) {
    flushTimer = setInterval(() => {
      void flushEvents();
    }, FLUSH_INTERVAL_MS);
  }
  if (visibilityHandler === null && typeof document !== "undefined") {
    visibilityHandler = () => {
      if (document.visibilityState === "hidden") {
        void flushEvents();
      }
    };
    document.addEventListener("visibilitychange", visibilityHandler);
  }
}

/** DUE-471: the device-level opt-out. `true` until the stored choice says
 * otherwise; `track()` stays synchronous, so the stored value is read once,
 * lazily, and a stored `false` empties whatever was buffered meanwhile. */
let analyticsEnabled = true;
let preferenceLoad: Promise<boolean> | null = null;

function loadPreference(): Promise<boolean> {
  if (preferenceLoad === null) {
    preferenceLoad = (async () => {
      try {
        const stored = await getPlatform().storage.getItem(ANALYTICS_ENABLED_KEY);
        if (stored === "false") {
          analyticsEnabled = false;
          buffer = [];
        } else if (stored === "true") {
          analyticsEnabled = true;
        }
      } catch {
        /* storage unavailable: the default (on) stands */
      }
      return analyticsEnabled;
    })();
  }
  return preferenceLoad;
}

/** Resolves to the stored choice (default `true`). */
export function getAnalyticsEnabled(): Promise<boolean> {
  return loadPreference();
}

/** Persists the choice on this device; `false` also drops the buffer. */
export async function setAnalyticsEnabled(enabled: boolean): Promise<void> {
  analyticsEnabled = enabled;
  preferenceLoad = Promise.resolve(enabled);
  if (!enabled) buffer = [];
  try {
    await getPlatform().storage.setItem(ANALYTICS_ENABLED_KEY, String(enabled));
  } catch {
    /* the choice still holds for this session */
  }
}

export function track(feature: string, action: string, props?: Record<string, unknown>): void {
  void loadPreference();
  if (!analyticsEnabled) return;
  buffer.push({ feature, action, props, client_ts: new Date().toISOString() });
  if (buffer.length > MAX_BUFFER_SIZE) {
    buffer.splice(0, buffer.length - MAX_BUFFER_SIZE);
  }
  ensureFlushScheduled();
}

export async function flushEvents(): Promise<void> {
  void loadPreference();
  if (!analyticsEnabled) {
    buffer = [];
    return;
  }
  if (buffer.length === 0) return;

  const jwt = useSessionStore.getState().jwt;
  if (!jwt) return; // no-op: keep buffering until a session exists

  const events = buffer;
  buffer = [];

  try {
    await fetch(`${API_BASE_URL}/api/events/batch`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({ platform: getPlatform().name, events }),
      keepalive: true,
    });
  } catch {
    // Fire-and-forget: drop the batch on any failure, no retry.
  }
}

/** Fires a `screen_view` event once per component mount, ignoring
 * re-renders (including a changed `feature` prop) that happen without
 * an unmount/remount in between. */
export function useScreenView(feature: string, props?: Record<string, unknown>): void {
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    track(feature, ACTIONS.screenView, props);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/** Testing hook: clears the buffer and tears down the interval/listener so
 * each test starts from a clean module state. */
export function __resetAnalyticsForTests(): void {
  buffer = [];
  analyticsEnabled = true;
  preferenceLoad = null;
  if (flushTimer !== null) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  if (visibilityHandler !== null && typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", visibilityHandler);
    visibilityHandler = null;
  }
}
